import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { StatusBadge, TemperatureBadge, fmtDate, slugToLabel } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Phone, MessageCircle, ArrowLeft, Loader2, Clock, MapPin, Mail, Tag,
  History, MessageSquare, CalendarClock, Activity, ShieldAlert, ShieldOff,
  Flag, Archive, ArchiveRestore, Users, Link2, Megaphone,
} from "lucide-react";
import { toast } from "sonner";

export default function LeadDetail({ mode = "admin" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [remark, setRemark] = useState("");
  const [fuDate, setFuDate] = useState("");
  const [fuNote, setFuNote] = useState("");
  const [temp, setTemp] = useState("");
  const [busy, setBusy] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [related, setRelated] = useState([]);
  const [reassignTo, setReassignTo] = useState("");
  const [reassignReason, setReassignReason] = useState("");

  const load = useCallback(async () => {
    try {
      const [d, s] = await Promise.all([api.get(`/leads/${id}`), api.get(`/lead-statuses`)]);
      setData(d.data);
      setTemp(d.data.lead.lead_temperature);
      setStatuses(s.data.items);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      navigate(-1);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (mode === "admin") {
      api.get("/employees").then((r) => setEmployees(r.data.items)).catch(() => {});
      api.get(`/leads/${id}/related`).then((r) => setRelated(r.data.items)).catch(() => {});
    }
  }, [id, mode]);

  if (!data) {
    return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>;
  }
  const { lead, activities, remarks, followups, assignment_history, assigned_employee } = data;
  const statusMeta = statuses.find((s) => s.slug === lead.current_status);

  const changeStatus = async (slug) => {
    setBusy(true);
    try {
      await api.patch(`/leads/${id}/status`, { status_slug: slug });
      toast.success("Status updated");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  const changeTemp = async (t) => {
    setTemp(t);
    try {
      await api.patch(`/leads/${id}`, { lead_temperature: t });
      toast.success("Temperature updated");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const patchLead = async (payload, msg) => {
    try { await api.patch(`/leads/${id}`, payload); toast.success(msg); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const reassign = async () => {
    if (!reassignTo) return toast.error("Choose a telecaller");
    try {
      await api.post(`/leads/${id}/assign`, { employee_id: reassignTo, reason: reassignReason || "Admin reassignment" });
      toast.success("Lead reassigned"); setReassignReason(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addRemark = async () => {
    if (!remark.trim()) return;
    try {
      await api.post(`/leads/${id}/remarks`, { text: remark });
      setRemark("");
      toast.success("Remark added");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addFollowup = async () => {
    if (!fuDate) return toast.error("Pick a date & time");
    try {
      await api.post(`/leads/${id}/followups`, { scheduled_at: new Date(fuDate).toISOString(), note: fuNote });
      setFuDate(""); setFuNote("");
      toast.success("Follow-up scheduled");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doCall = async () => {
    try {
      const { data: r } = await api.post(`/leads/${id}/call`);
      if (r.tel_url) window.location.href = r.tel_url;
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doWhatsapp = async () => {
    try {
      const { data: r } = await api.post(`/leads/${id}/whatsapp`);
      if (r.whatsapp_url) window.open(r.whatsapp_url, "_blank");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const completeFollowup = async (fid) => {
    try {
      await api.patch(`/followups/${fid}`, { status: "COMPLETED" });
      toast.success("Follow-up completed");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="animate-fade-up space-y-5">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-button">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      {/* Header card */}
      <div className="glass rounded-xl p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-2xl font-bold">{lead.name}</h1>
              <span className="font-mono text-xs text-muted-foreground">#{lead.lead_number}</span>
              <TemperatureBadge temp={lead.lead_temperature} />
              {statusMeta && <StatusBadge status={statusMeta.name} colorToken={statusMeta.color_token} />}
              {lead.is_dnc && (
                <span className="inline-flex items-center gap-1 text-xs text-red-400 border border-red-500/40 rounded-full px-2 py-0.5">
                  <ShieldAlert className="h-3 w-3" /> DNC
                </span>
              )}
              {lead.possible_duplicate && (
                <span className="text-[11px] text-amber-400 border border-amber-500/30 rounded-full px-2 py-0.5">possible duplicate</span>
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {lead.mobile}</span>
              {lead.email && <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {lead.email}</span>}
              {lead.city && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {lead.city} {lead.pincode}</span>}
              {lead.product_interest && <span className="flex items-center gap-1.5"><Tag className="h-3.5 w-3.5" /> {lead.product_interest}</span>}
              <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {fmtDate(lead.created_at)}</span>
            </div>
            {assigned_employee && (
              <div className="mt-2 text-xs text-muted-foreground">Assigned to <span className="text-foreground font-medium">{assigned_employee.name}</span> · Source: <span className="text-foreground">{lead.source}</span>{lead.campaign ? ` · ${lead.campaign}` : ""}</div>
            )}
          </div>

          <div className="flex gap-2">
            <Button onClick={doCall} data-testid="lead-call-action-button" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Phone className="h-4 w-4 mr-1.5" /> Call
            </Button>
            <Button onClick={doWhatsapp} data-testid="lead-whatsapp-action-button" variant="outline" className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10">
              <MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp
            </Button>
          </div>
        </div>

        {/* Quick controls */}
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-muted-foreground">Change Status</Label>
            <Select value={lead.current_status} onValueChange={changeStatus} disabled={busy}>
              <SelectTrigger data-testid="lead-status-select" className="mt-1 bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 max-h-72">
                {statuses.map((s) => (
                  <SelectItem key={s.slug} value={s.slug} data-testid={`status-option-${s.slug}`}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Lead Temperature</Label>
            <Select value={temp} onValueChange={changeTemp}>
              <SelectTrigger data-testid="lead-temperature-select" className="mt-1 bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700">
                {["HOT", "WARM", "COLD", "UNCLASSIFIED"].map((t) => (
                  <SelectItem key={t} value={t} data-testid={`temp-option-${t}`}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Admin management */}
      {mode === "admin" && (
        <div className="glass rounded-xl p-5 space-y-5">
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><Flag className="h-3 w-3" /> Priority</Label>
              <Select value={lead.priority || "NORMAL"} onValueChange={(p) => patchLead({ priority: p }, "Priority updated")}>
                <SelectTrigger data-testid="lead-priority-select" className="mt-1 bg-slate-900/60 border-slate-700"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {["NORMAL", "HIGH", "URGENT"].map((p) => <SelectItem key={p} value={p} data-testid={`priority-option-${p}`}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col justify-end">
              <Label className="text-xs text-muted-foreground">Do-Not-Call</Label>
              <Button variant="outline" data-testid="toggle-dnc-button" onClick={() => patchLead({ is_dnc: !lead.is_dnc }, lead.is_dnc ? "DNC removed" : "Marked DNC")}
                className={`mt-1 border-slate-700 ${lead.is_dnc ? "text-red-400 border-red-500/40" : ""}`}>
                <ShieldOff className="h-4 w-4 mr-1.5" /> {lead.is_dnc ? "Remove DNC" : "Mark DNC"}
              </Button>
            </div>
            <div className="flex flex-col justify-end">
              <Label className="text-xs text-muted-foreground">Archive</Label>
              <Button variant="outline" data-testid="toggle-archive-button" onClick={() => patchLead({ is_archived: !lead.is_archived }, lead.is_archived ? "Lead restored" : "Lead archived")}
                className="mt-1 border-slate-700">
                {lead.is_archived ? <><ArchiveRestore className="h-4 w-4 mr-1.5" /> Restore</> : <><Archive className="h-4 w-4 mr-1.5" /> Archive</>}
              </Button>
            </div>
          </div>

          {/* Reassign */}
          <div className="grid sm:grid-cols-3 gap-3 items-end border-t border-slate-800 pt-4">
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><Users className="h-3 w-3" /> Reassign to</Label>
              <Select value={reassignTo} onValueChange={setReassignTo}>
                <SelectTrigger data-testid="reassign-select" className="mt-1 bg-slate-900/60 border-slate-700"><SelectValue placeholder="Choose telecaller" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} · {e.assignment_status}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input value={reassignReason} onChange={(e) => setReassignReason(e.target.value)} placeholder="Reason (optional)" data-testid="reassign-reason" className="bg-slate-900/60 border-slate-700" />
            <Button onClick={reassign} data-testid="reassign-button" className="bg-primary text-primary-foreground">Reassign</Button>
          </div>

          {/* Attribution */}
          <div className="border-t border-slate-800 pt-4">
            <div className="flex items-center gap-2 text-sm font-semibold mb-2"><Megaphone className="h-4 w-4 text-primary" /> Attribution</div>
            <div className="grid sm:grid-cols-3 gap-x-6 gap-y-1.5 text-xs">
              {[["Source", lead.source], ["Original Source", lead.original_source], ["Campaign", lead.campaign],
                ["Ad Set", lead.ad_set_id], ["Ad", lead.ad_id], ["Landing Page", lead.landing_page],
                ["UTM Source", lead.utm_source], ["UTM Medium", lead.utm_medium], ["UTM Campaign", lead.utm_campaign],
                ["UTM Content", lead.utm_content], ["UTM Term", lead.utm_term], ["fbclid", lead.fbclid]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-2 border-b border-slate-800/50 py-0.5">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="font-mono text-right truncate max-w-[60%]" title={v || ""}>{v || "—"}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Related leads (duplicate intelligence) */}
          {related.length > 0 && (
            <div className="border-t border-slate-800 pt-4">
              <div className="flex items-center gap-2 text-sm font-semibold mb-2"><Link2 className="h-4 w-4 text-amber-400" /> {related.length} related lead(s) — same mobile/email</div>
              <div className="space-y-2">
                {related.map((r) => (
                  <button key={r.id} onClick={() => navigate(`/admin/leads/${r.id}`)} data-testid={`related-lead-${r.id}`}
                    className="w-full text-left rounded-lg bg-slate-900/50 border border-slate-800 p-2.5 hover:border-slate-600 flex items-center justify-between">
                    <span className="text-xs"><span className="font-mono text-primary">{r.lead_code}</span> · {r.name} · {r.current_status}</span>
                    <span className="text-[11px] text-muted-foreground">{fmtDate(r.created_at)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="timeline">
        <TabsList className="bg-slate-900/60 border border-slate-800">
          <TabsTrigger value="timeline" data-testid="tab-timeline"><Activity className="h-4 w-4 mr-1.5" />Timeline</TabsTrigger>
          <TabsTrigger value="remarks" data-testid="tab-remarks"><MessageSquare className="h-4 w-4 mr-1.5" />Remarks</TabsTrigger>
          <TabsTrigger value="followups" data-testid="tab-followups"><CalendarClock className="h-4 w-4 mr-1.5" />Follow-ups</TabsTrigger>
          {mode === "admin" && <TabsTrigger value="history" data-testid="tab-history"><History className="h-4 w-4 mr-1.5" />Assignment</TabsTrigger>}
        </TabsList>

        <TabsContent value="timeline" className="mt-4">
          <div className="glass rounded-xl p-5 space-y-4">
            {activities.length === 0 && <p className="text-sm text-muted-foreground">No activity yet.</p>}
            {activities.map((a) => (
              <div key={a.id} className="flex gap-3 border-l-2 border-slate-700 pl-4 pb-1" data-testid={`activity-${a.id}`}>
                <div className="h-2 w-2 rounded-full bg-primary -ml-[21px] mt-1.5" />
                <div>
                  <div className="text-sm text-foreground">{a.description}</div>
                  <div className="text-[11px] text-muted-foreground font-mono">{slugToLabel(a.activity_type)} · {fmtDate(a.created_at)}</div>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="remarks" className="mt-4">
          <div className="glass rounded-xl p-5">
            <div className="flex gap-2">
              <Textarea value={remark} onChange={(e) => setRemark(e.target.value)} placeholder="Add a remark…" data-testid="remark-input" className="bg-slate-900/60 border-slate-700" />
              <Button onClick={addRemark} data-testid="add-remark-button" className="bg-primary text-primary-foreground self-end">Add</Button>
            </div>
            <div className="mt-4 space-y-3">
              {remarks.length === 0 && <p className="text-sm text-muted-foreground">No remarks yet.</p>}
              {remarks.map((r) => (
                <div key={r.id} className="rounded-lg bg-slate-900/50 border border-slate-800 p-3" data-testid={`remark-${r.id}`}>
                  <div className="text-sm">{r.text}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">{r.user_name} · {fmtDate(r.created_at)}</div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="followups" className="mt-4">
          <div className="glass rounded-xl p-5">
            <div className="grid sm:grid-cols-3 gap-2 items-end">
              <div>
                <Label className="text-xs text-muted-foreground">Date & time</Label>
                <Input type="datetime-local" value={fuDate} onChange={(e) => setFuDate(e.target.value)} data-testid="followup-date-input" className="bg-slate-900/60 border-slate-700 mt-1" />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Note</Label>
                <Input value={fuNote} onChange={(e) => setFuNote(e.target.value)} placeholder="Optional note" data-testid="followup-note-input" className="bg-slate-900/60 border-slate-700 mt-1" />
              </div>
              <Button onClick={addFollowup} data-testid="add-followup-button" className="bg-primary text-primary-foreground">Schedule</Button>
            </div>
            <div className="mt-4 space-y-3">
              {followups.length === 0 && <p className="text-sm text-muted-foreground">No follow-ups scheduled.</p>}
              {followups.map((f) => (
                <div key={f.id} className="flex items-center justify-between rounded-lg bg-slate-900/50 border border-slate-800 p-3" data-testid={`followup-${f.id}`}>
                  <div>
                    <div className="text-sm">{fmtDate(f.scheduled_at)}</div>
                    {f.note && <div className="text-xs text-muted-foreground">{f.note}</div>}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full border ${f.status === "COMPLETED" ? "text-emerald-400 border-emerald-500/30" : "text-amber-400 border-amber-500/30"}`}>{f.status}</span>
                    {f.status === "PENDING" && (
                      <Button size="sm" variant="ghost" onClick={() => completeFollowup(f.id)} data-testid={`complete-followup-${f.id}`} className="text-primary">Done</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {mode === "admin" && (
          <TabsContent value="history" className="mt-4">
            <div className="glass rounded-xl p-5 space-y-3">
              {assignment_history.length === 0 && <p className="text-sm text-muted-foreground">No assignment history.</p>}
              {assignment_history.map((h) => (
                <div key={h.id} className="rounded-lg bg-slate-900/50 border border-slate-800 p-3 text-sm" data-testid={`history-${h.id}`}>
                  <span className="font-mono text-xs text-primary">{h.assignment_type}</span> — {h.reason || "—"}
                  <div className="text-[11px] text-muted-foreground mt-1">{fmtDate(h.created_at)}</div>
                </div>
              ))}
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
